import { Injectable, OnDestroy } from '@angular/core';
//import { LabelOverlay } from './label.overlay';
import { Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { Layer } from '../store/cop/models/layer.model';
import { MapLabelFactory } from './label-factory';

const MAX_LABELS_TO_CHECK = 5000;
export const MIN_LABELS_ZOOM = 8;

@Injectable({
  providedIn: 'root'
})
export class MapLabelService implements OnDestroy {
  private layerLabels: layerLabels;
  private labelFactory: MapLabelFactory;
  private initDone: boolean;
  private checkLabels$: Subject<{
    layerUId: string;
    minZoomLevel: number;
    labels: {
      label: any;
    }[];
  }> = new Subject();
  destroy$: Subject<void> = new Subject();

  constructor() {
    this.initDone == false;
    this.init();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private init() {
    if (this.initDone === true) {
      return;
    }

    this.layerLabels = {};

    try {
      if (typeof google === undefined || typeof google.maps === undefined) {
        return;
      }
    } catch (e) {
      return;
    }

    this.labelFactory = new MapLabelFactory();
    this.checkLabels$.pipe(takeUntil(this.destroy$), tap(data => this.processLabelsChunk(data.layerUId, data.minZoomLevel, data.labels))).subscribe();
    this.initDone = true;
  }

  public addMarkerLabel(layerUId: string, minZoomLevel: number, label: any): void {
    if (!!!this.layerLabels[layerUId]) {
      this.layerLabels[layerUId] = {
        minZoomLevel: minZoomLevel,
        markerLabels: {}
      };
    }

    this.layerLabels[layerUId].minZoomLevel = minZoomLevel;

    const marker = label.getMarker();

    this.layerLabels[layerUId].markerLabels[marker['__data'].id] = {
      label: label
    };

    this.checkMarkerLabelDisplay(layerUId, minZoomLevel, label);
  }

  public getMarkerLabel(layerUId: string, marker: google.maps.Marker): any {
    if (!!!this.layerLabels[layerUId]) {
      return null;
    }

    const id = marker['__data'].id;
    if (!!!this.layerLabels[layerUId].markerLabels[id]) {
      return null;
    }

    return this.layerLabels[layerUId].markerLabels[id].label;
  }

  public checkMarkerLabelDisplay(layerUId: string, minZoomLevel: number, label: any) {
    const markerMap = label.getMarkerMap();

    const inZoom = markerMap ? markerMap.getZoom() >= minZoomLevel : false;
    const showLabel = inZoom ? (markerMap as google.maps.Map)?.getBounds()?.contains(label.getPosition()) : false;

    if (showLabel) {
      label.showLabel();
    } else {
      label.hideLabel();
    }
  }

  public checkLayerLabelsDisplay(layerUId: string) {
    if (!!!this.layerLabels[layerUId]) {
      return;
    }

    let len = Object.values(this.layerLabels[layerUId].markerLabels).length;

    const minZoomLevel = this.layerLabels[layerUId].minZoomLevel;

    for (let i = 0; i < len; i += MAX_LABELS_TO_CHECK) {
      const labelsChunk = Object.values(this.layerLabels[layerUId].markerLabels).slice(i, i + MAX_LABELS_TO_CHECK);

      setTimeout(
        () =>
          this.checkLabels$.next({
            layerUId,
            minZoomLevel,
            labels: labelsChunk
          }),
        100
      );
    }
  }

  private processLabelsChunk(
    layerUId: string,
    minZoomLevel: number,
    labels: {
      label: any;
    }[]
  ) {
    const len = labels.length;
    for (let i = 0; i < len; i++) {
      this.checkMarkerLabelDisplay(layerUId, minZoomLevel, labels[i].label);
    }
  }

  public deleteMarkerLabel(layerUId: string, marker: google.maps.Marker) {
    if (!!!this.layerLabels[layerUId]) {
      return;
    }

    const id = marker['__data'].id;
    if (!!!this.layerLabels[layerUId].markerLabels[id]) {
      return;
    }
    const lb = this.layerLabels[layerUId].markerLabels[id].label;
    lb.unbindAll();
    lb.onRemove();
    lb.setMap(null);
    delete this.layerLabels[layerUId].markerLabels[id];
  }

  public getLayerLabelMinZoom(layer: Layer): number {
    return !!layer.displayOptions?.minLabelZoom ? layer.displayOptions?.minLabelZoom : MIN_LABELS_ZOOM;
  }

  public deleteLayerLabels(layerUId: string) {
    if (!!!this.layerLabels[layerUId]) {
      return;
    }

    Object.values(this.layerLabels[layerUId].markerLabels).forEach(d => {
      this.deleteMarkerLabel(layerUId, d.label.getMarker());
    });
    delete this.layerLabels[layerUId];
  }

  public deleteAll() {
    Object.keys(this.layerLabels).forEach(l => {
      this.deleteLayerLabels(l);
    });
    this.layerLabels = {};
  }

  public createLabel(
    marker: google.maps.Marker,
    label: string,
    layerUId: string,
    documentUId: string,
    offset?: { left?: number; top?: number },
    divClassName?: string,
    zIndex?: number,
    borderColor?: string,
    backColor?: string
  ): any {
    this.init();
    return this.labelFactory?.createLabel(marker, label, layerUId, documentUId, offset, divClassName, zIndex, borderColor, backColor);
  }
}

interface markerLabels {
  [uid: string]: {
    label: any;
  };
}

interface layerLabels {
  [layerUId: string]: {
    minZoomLevel: number;
    markerLabels: markerLabels;
  };
}
